import React from "react";
import {
  getBriefing,
  getExecutionPreview,
  postTabDecision,
  postExecute,
} from "./api/tabClient";

const cx = (...c) => c.filter(Boolean).join(" ");

function Section({ title, subtitle, right, children, className }) {
  return (
    <section className={cx("rounded-2xl border border-gray-200 bg-white shadow-sm", className)}>
      {(title || subtitle || right) && (
        <header className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
            {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
          </div>
          {right}
        </header>
      )}
      <div className="p-4">{children}</div>
    </section>
  );
}

function Pill({ children, tone = "gray" }) {
  const map = {
    gray: "bg-gray-100 text-gray-800",
    blue: "bg-blue-100 text-blue-800",
    green: "bg-green-100 text-green-800",
    red: "bg-red-100 text-red-800",
    amber: "bg-amber-100 text-amber-800",
  };
  return (
    <span className={cx("inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium", map[tone])}>
      {children}
    </span>
  );
}

export default function TABDecisionPanel({ demandId }) {
  // data
  const [loading, setLoading] = React.useState(true);
  const [briefing, setBriefing] = React.useState(null);
  const [preview, setPreview] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [notice, setNotice] = React.useState(null);
  const [execResult, setExecResult] = React.useState(null);

  // decision state
  const [decision, setDecision] = React.useState("APPROVE_WITH_CONDITIONS"); // APPROVE | REJECT | APPROVE_WITH_CONDITIONS
  // Starts empty. It used to be pre-seeded with "Add audit log plan", which meant
  // an unattended Save recorded a condition nobody had asked for — and that
  // condition now becomes a real task in ServiceNow.
  const [conditions, setConditions] = React.useState([]);
  // ServiceNow is the default because it is the tool that has an integration;
  // choosing Jira returns a 501.
  const [tool, setTool] = React.useState("servicenow");
  const [template, setTemplate] = React.useState("GovProj-Standard");
  const [rationale, setRationale] = React.useState("");

  React.useEffect(() => {
    if (!demandId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const [b, p] = await Promise.all([getBriefing(demandId), getExecutionPreview(demandId)]);
        if (cancelled) return;
        setBriefing(b);
        setPreview(p);
        // Adopt the AI's suggested conditions as a starting point, clearly sourced.
        if (Array.isArray(b?.suggestedConditions) && b.suggestedConditions.length) {
          setConditions(b.suggestedConditions.map((c) => ({
            id: crypto.randomUUID(),
            title: c.title || "",
            dueDays: Number(c.dueDays) || 7,
          })));
        }
        if (b?.recommendation) setDecision(b.recommendation);
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [demandId]);

  const addCondition = () =>
    setConditions((c) => [...c, { id: crypto.randomUUID(), title: "", dueDays: 7 }]);
  const removeCondition = (id) => setConditions((c) => c.filter((x) => x.id !== id));
  const updateCondition = (id, patch) =>
    setConditions((c) => c.map((x) => (x.id === id ? { ...x, ...patch } : x)));

  const canSave =
    decision === "REJECT" ||
    decision === "APPROVE" ||
    // conditions.every() is true for an empty array, so the old check let you save
    // APPROVE_WITH_CONDITIONS with no conditions — which the server rejects (422).
    (decision === "APPROVE_WITH_CONDITIONS" &&
      conditions.length > 0 &&
      conditions.every((c) => c.title.trim()));

  const onSubmitDecision = async () => {
    try {
      setError(null);
      const payload =
        decision === "APPROVE_WITH_CONDITIONS"
          ? {
              decision,
              conditions: conditions.map(({ title, dueDays }) => ({ title, dueDays: Number(dueDays) || 7 })),
              targetExecution: { tool, template },
              rationale,
            }
          : {
              decision,
              targetExecution: decision === "APPROVE" ? { tool, template } : undefined,
              rationale,
            };

      const res = await postTabDecision(demandId, payload);
      setNotice(res?.message || "TAB decision recorded.");
      // The decision changes what a handoff would produce, so refresh the preview.
      const refreshed = await getExecutionPreview(demandId).catch(() => null);
      if (refreshed) setPreview(refreshed);
    } catch (e) {
      setError(e.message || String(e));
    }
  };

  const onExecute = async () => {
    try {
      setError(null);
      setNotice(null);
      const res = await postExecute(demandId, { tool, template });
      setNotice(res?.message || "Execution objects created.");
      setExecResult(res);
    } catch (e) {
      // Jira reports 501 rather than pretending; 409 means TAB hasn't approved yet.
      setError(
        e.status === 501 ? `${e.message} Switch the target tool to ServiceNow.`
        : e.status === 409 ? `${e.message} Save an approval decision first.`
        : e.status === 503 ? `${e.message} Check /api/servicenow/health.`
        : e.message || String(e)
      );
    }
  };

  return (
    <div className="mx-auto max-w-7xl gap-4 p-4 lg:grid lg:grid-cols-3">
      {/* Left: Briefing & decision */}
      <div className="lg:col-span-2 space-y-4">
        {/* Sticky page header */}
        <div className="sticky top-0 z-10 -mx-4 mb-2 border-b border-gray-200 bg-white/90 backdrop-blur">
          <div className="mx-4 flex items-center justify-between py-3">
            <div>
              {/* <h1 className="text-xl font-semibold tracking-tight text-gray-900">TAB Decision</h1> */}
              <p className="text-xs text-gray-500">Demand: {demandId}</p>
            </div>
            <div className="flex items-center gap-2">
              <Pill tone="blue">Final decision</Pill>
            </div>
          </div>
        </div>

        <Section
          title="TAB Briefing"
          subtitle="AI-prepared summary across Discovery, POC, Requirements, and Security"
          right={<Pill>{loading ? "Loading…" : "Ready"}</Pill>}
        >
          {error && (
            <div className="mb-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">{error}</div>
          )}
          {notice && (
            <div className="mb-3 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-900">{notice}</div>
          )}

          {!loading && briefing && (
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-3">
                {/* Which stages produced nothing. A board needs to know what it is
                    deciding without — the previous fallback briefing asserted
                    "POC met success metrics" for demands that had no POC at all. */}
                {briefing.missingEvidence?.length > 0 && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                    <div className="font-medium">Deciding without evidence from:</div>
                    <ul className="mt-1 list-inside list-disc">
                      {briefing.missingEvidence.map((m, i) => <li key={i}>{m}</li>)}
                    </ul>
                  </div>
                )}
                {briefing.recommendation && (
                  <div className="text-sm">
                    <span className="font-medium">AI recommendation:</span>{" "}
                    <Pill tone={briefing.recommendation === "APPROVE" ? "green"
                                : briefing.recommendation === "REJECT" ? "red" : "amber"}>
                      {briefing.recommendation}
                    </Pill>
                  </div>
                )}
                <h4 className="text-xl font-semibold text-gray-900">Executive Summary</h4>
                <div className="whitespace-pre-wrap rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-800">
                  {briefing.summary || "—"}
                </div>

                {Array.isArray(briefing.stitchedSections) && (
                  <div className="space-y-2">
                    {briefing.stitchedSections.map((s, i) => (
                      <div key={i} className="rounded-lg border border-gray-200 p-3">
                        <div className="text-sm font-medium text-gray-900">{s.title}</div>
                        <div className="text-sm text-gray-700">{s.note}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-xl font-semibold text-gray-900">Briefing Deck (PDF)</div>
                  {briefing.pdfUrl && (
                    <a
                      className="text-xs text-indigo-600 hover:underline"
                      href={briefing.pdfUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open in new tab
                    </a>
                  )}
                </div>
                {briefing.pdfUrl ? (
                  <iframe
                    title="Briefing PDF"
                    src={briefing.pdfUrl}
                    className="h-80 w-full rounded-lg border"
                  />
                ) : (
                  <div className="h-80 w-full rounded-lg border border-dashed p-3 text-sm text-gray-500">
                    No PDF URL provided. Upload or generate via backend to preview here.
                  </div>
                )}
              </div>
            </div>
          )}
        </Section>

        <Section
          title="Decision Panel"
          subtitle="Approve / Reject / Approve with conditions; choose execution target"
        >
          {/* Decision radio */}
          <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
            {[
              { id: "APPROVE", label: "Approve", tone: "green" },
              { id: "APPROVE_WITH_CONDITIONS", label: "Approve with Conditions", tone: "amber" },
              { id: "REJECT", label: "Reject", tone: "red" },
            ].map((opt) => (
              <label
                key={opt.id}
                className={cx(
                  "flex cursor-pointer items-center gap-2 rounded-xl border p-3 hover:bg-gray-50",
                  decision === opt.id && "border-gray-900"
                )}
              >
                <input
                  type="radio"
                  name="tab-decision"
                  className="h-4 w-4"
                  checked={decision === opt.id}
                  onChange={() => setDecision(opt.id)}
                />
                <span className="text-sm font-medium text-gray-900">{opt.label}</span>
                <span className="ml-auto">
                  <Pill tone={opt.tone}>{opt.id}</Pill>
                </span>
              </label>
            ))}
          </div>

          {/* Conditions grid */}
          {decision === "APPROVE_WITH_CONDITIONS" && (
            <div className="mb-4">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-sm font-medium text-gray-900">Conditions</div>
                <button
                  onClick={addCondition}
                  className="rounded-md border border-gray-300 px-3 py-1 text-xs hover:bg-gray-50"
                >
                  + Add Condition
                </button>
              </div>
              <div className="space-y-2">
                {conditions.map((c) => (
                  <div key={c.id} className="grid grid-cols-6 items-center gap-2">
                    <input
                      className="col-span-4 rounded-lg border border-gray-300 px-3 py-2 text-sm"
                      placeholder="Condition title (task to enforce the condition)"
                      value={c.title}
                      onChange={(e) => updateCondition(c.id, { title: e.target.value })}
                    />
                    <input
                      type="number"
                      min="1"
                      className="col-span-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
                      placeholder="Due (days)"
                      value={c.dueDays}
                      onChange={(e) => updateCondition(c.id, { dueDays: e.target.value })}
                    />
                    <button
                      onClick={() => removeCondition(c.id)}
                      className="col-span-1 rounded-md border border-gray-300 px-2 py-2 text-xs text-rose-600 hover:bg-gray-50"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Conditions will be encoded as tasks in AIEA and the execution tool.
              </p>
            </div>
          )}

          {/* Target execution tool */}
          {decision !== "REJECT" && (
            <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
              <div>
                <div className="mb-1 text-xs font-medium text-gray-600">Target Tool</div>
                <select
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  value={tool}
                  onChange={(e) => setTool(e.target.value)}
                >
                  <option value="servicenow">ServiceNow</option>
                  <option value="jira">Jira (not integrated)</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <div className="mb-1 text-xs font-medium text-gray-600">Project Template</div>
                <input
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  placeholder="e.g., GovProj-Standard"
                  value={template}
                  onChange={(e) => setTemplate(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Rationale */}
          <div className="mb-4">
            <div className="mb-1 text-xs font-medium text-gray-600">Rationale / Notes (optional)</div>
            <textarea
              rows={3}
              className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm"
              placeholder="Add supporting notes for the decision record…"
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
            />
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2">
            <button
              disabled={!canSave}
              onClick={onSubmitDecision}
              className={cx(
                "rounded-lg px-4 py-2 text-sm font-medium text-white",
                !canSave ? "bg-gray-400 cursor-not-allowed" : "bg-gray-900 hover:bg-gray-800"
              )}
            >
              Save Decision
            </button>

            {decision !== "REJECT" && (
              <button
                onClick={onExecute}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium hover:bg-gray-50"
              >
                Execute (Create Objects)
              </button>
            )}
          </div>
        </Section>
          {/* Right: Execution preview */}
      <div className="space-y-4">
        <Section title="Handoff Preview" subtitle="Objects that will be created in the target tool">
          {!loading && preview ? (
            <div className="space-y-2">
              <div className="text-sm">
                Tool: <span className="font-medium">{preview.tool}</span>
                {" "}• Template: <span className="font-medium">{template}</span>
              </div>
              {preview.alreadyCreated && (
                <div className="rounded-md border border-blue-200 bg-blue-50 p-2 text-xs text-blue-900">
                  Already handed off as {preview.existing?.projectId}. Executing again is blocked.
                </div>
              )}
              <div className="rounded-lg border" style={{ overflowX: 'auto' }}>
                <table className="min-w-full text-sm" style={{width:'100%'}}>
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Type</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Table</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Summary</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.willCreate?.map((row, i) => (
                      <tr key={i} className="border-t">
                        <td className="px-3 py-2">{row.type}</td>
                        <td className="px-3 py-2 font-mono text-xs text-gray-600">{row.table}</td>
                        <td className="px-3 py-2">{row.summary}</td>
                      </tr>
                    ))}
                    {!preview.willCreate?.length && (
                      <tr>
                        <td className="px-3 py-4 text-gray-500" colSpan={3}>
                          No preview items.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {preview.servicenow && !preview.servicenow.enabled && (
                <p className="text-xs text-amber-700">
                  ServiceNow calls are currently{" "}
                  {preview.servicenow.configured ? "disabled (USE_REAL_SN=false)" : "unconfigured"} —
                  Execute will return 503 until that is fixed.
                </p>
              )}
              {execResult?.links?.projectUrl && (
                <p className="text-xs">
                  Created:{" "}
                  <a className="text-indigo-600 hover:underline" href={execResult.links.projectUrl}
                     target="_blank" rel="noreferrer">{execResult.projectId}</a>
                </p>
              )}
            </div>
          ) : (
            <div className="text-sm text-gray-500">Loading…</div>
          )}
        </Section>
      </div>
      </div>

    
    </div>
  );
}
